'use client';

import { useMemo, useState, type MouseEvent } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useIntl } from 'react-intl';
import { DocumentTypeCode } from '@/apiClient';
import type { RoleUserOrgAssociationDto } from '@/apiClient';
import { usePrivate } from '@/components/private';
import { useShell } from '@/providers';
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@/primitives';
import { DocumentImage } from '@/components/avatars/DocumentImage';
import {
  FOOTER_HEIGHT,
  MOBILE_FOOTER_HEIGHT,
  NAV_HEIGHT,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
} from '@/configs/layout';
import { ERoute } from '@/configs/routes';
import noLogoSvg from '@/components/organisations/noLogo.svg';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import ShellSidebarLinks from './ShellSidebarLinks';
import { registrySidebarDarkBackground } from '@/lib/registrySidebarDarkBg';
import { alpha, useTheme } from '@mui/material/styles';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import { blue, white } from '@/lib/colours';
import {
  registrySidebarDarkRailForeground,
  registrySidebarLightRailForeground,
} from '@/lib/registrySidebarRail';
import {
  getRegistryPathSuffix,
  sanitizePathSuffixForOrgSwitch,
} from '@/utils/registryNavigation';
import {
  getRecentOrganisationIds,
  storeSelectedOrganisation,
} from '@/utils/storage';

const RECENT_DISPLAY_LIMIT = 5;
const SEARCH_RESULTS_LIMIT = 10;

function orderAndFilterOrgs(
  userOrganisations: RoleUserOrgAssociationDto[],
  recentIds: number[],
  searchQuery: string,
  limit: number,
): RoleUserOrgAssociationDto[] {
  const search = searchQuery.trim().toLowerCase();
  const byId = new Map(userOrganisations.map((o) => [o.organisationId, o]));
  const recentOrdered = recentIds
    .map((id) => byId.get(id))
    .filter(Boolean) as RoleUserOrgAssociationDto[];
  const remaining = userOrganisations.filter(
    (o) =>
      o.organisationId === undefined || !recentIds.includes(o.organisationId),
  );
  const ordered = [...recentOrdered, ...remaining];
  const filtered = search
    ? ordered.filter((o) => {
        const name = o.organisation?.name?.toLowerCase() ?? '';
        const desc = o.organisation?.description?.toLowerCase() ?? '';
        return name.includes(search) || desc.includes(search);
      })
    : ordered;
  return filtered.slice(0, limit);
}

export default function ShellSidebar() {
  const theme = useTheme();
  const { isDarkMode } = useResolvedColorScheme();
  const intl = useIntl();
  const pathname = usePathname();
  const router = useRouter();
  const {
    userOrganisations,
    selectedOrganisationId,
    setSelectedOrganisation,
    setSelectedOrganisationId,
    setShowSplashScreen,
  } = usePrivate();
  const { sidebarExpanded, setConfigMenuOpen, setSidebarExpanded } = useShell();
  const [dropdownAnchor, setDropdownAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const selectedOrganisation = userOrganisations?.find(
    (o) => o.organisationId === selectedOrganisationId,
  );

  const recentIds = useMemo(() => getRecentOrganisationIds(), [dropdownAnchor]);
  const displayOrgs = useMemo(() => {
    if (!userOrganisations?.length) return [];
    const hasSearch = Boolean(searchQuery.trim());
    const limit = hasSearch
      ? SEARCH_RESULTS_LIMIT
      : showAll
        ? 999
        : RECENT_DISPLAY_LIMIT;
    return orderAndFilterOrgs(userOrganisations, recentIds, searchQuery, limit);
  }, [userOrganisations, recentIds, searchQuery, showAll]);

  const totalOrgsWhenNoSearch = useMemo(() => {
    if (!userOrganisations?.length || searchQuery.trim()) return 0;
    return orderAndFilterOrgs(userOrganisations, recentIds, '', 999).length;
  }, [userOrganisations, recentIds, searchQuery]);

  const showAllButtonVisible =
    !searchQuery.trim() &&
    totalOrgsWhenNoSearch > RECENT_DISPLAY_LIMIT &&
    !showAll;

  const societySelectorColor = isDarkMode
    ? registrySidebarDarkRailForeground
    : registrySidebarLightRailForeground;

  const expandRailControlColor = isDarkMode
    ? registrySidebarDarkRailForeground
    : registrySidebarLightRailForeground;

  const handleOpenDropdown = (event: MouseEvent<HTMLElement>) => {
    if (!userOrganisations?.length) return;
    setDropdownAnchor(event.currentTarget);
    setSearchQuery('');
    setShowAll(false);
  };

  const handleCloseDropdown = () => {
    setDropdownAnchor(null);
    setSearchQuery('');
    setShowAll(false);
  };

  const handleSelectOrg = (org: RoleUserOrgAssociationDto) => {
    const orgId = org.organisationId;
    if (orgId == null || orgId === selectedOrganisationId) {
      handleCloseDropdown();
      return;
    }
    if (
      !setSelectedOrganisation ||
      !setSelectedOrganisationId ||
      !setShowSplashScreen
    ) {
      handleCloseDropdown();
      return;
    }
    const organisation = org.organisation!;
    const orgName = organisation.name!;
    setSelectedOrganisation(organisation);
    setSelectedOrganisationId(orgId);
    setShowSplashScreen(true);
    storeSelectedOrganisation(orgId, orgName);
    handleCloseDropdown();
    const currentOrgName = selectedOrganisation?.organisation?.name;
    const pathSuffix =
      currentOrgName != null
        ? getRegistryPathSuffix(pathname ?? '', currentOrgName)
        : null;
    if (pathSuffix) {
      const safeSuffix = sanitizePathSuffixForOrgSwitch(pathSuffix);
      router.push(`/${orgName}/registry/${safeSuffix}`);
    } else {
      router.push(`/${orgName}/registry/${ERoute.DASHBOARD}`);
    }
  };

  return (
    <Box
      component="nav"
      sx={{
        boxSizing: 'border-box',
        position: 'relative',
        pt: '12px',
        pb: '10px',
        px: sidebarExpanded ? '10px' : '6px',
        height: '100%',
        minHeight: {
          xs: `calc(100vh - ${NAV_HEIGHT}px - ${MOBILE_FOOTER_HEIGHT}px)`,
          md: `calc(100vh - ${NAV_HEIGHT}px - ${FOOTER_HEIGHT}px)`,
        },
        width: `${sidebarExpanded ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED}px`,
        background: isDarkMode
          ? registrySidebarDarkBackground(theme)
          : blue[900],
        overflow: 'hidden',
        transition: 'width 0.1s ease-in-out',

        '@media print': {
          display: 'none',
        },
        color: isDarkMode
          ? registrySidebarDarkRailForeground
          : registrySidebarLightRailForeground,
      }}
    >
      {!userOrganisations?.length && (
        <Skeleton
          sx={{
            mb: '16px',
            border: `1px solid ${societySelectorColor}`,
            borderRadius: '4px',
            transform: 'none',
            boxSizing: 'border-box',
            ...(sidebarExpanded
              ? { height: '36px', width: '100%' }
              : {
                  height: '30px',
                  width: 'calc(100% - 16px)',
                  mx: 'auto',
                }),
          }}
        />
      )}
      {selectedOrganisation && (
        <Box
          aria-controls={dropdownAnchor ? 'society-dropdown-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={Boolean(dropdownAnchor)}
          onClick={handleOpenDropdown}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarExpanded ? 'flex-start' : 'center',
            gap: sidebarExpanded ? '4px' : 0,
            mb: '16px',
            border: `1px solid ${societySelectorColor}`,
            borderRadius: '4px',
            background: 'transparent',
            cursor: 'pointer',
            boxSizing: 'border-box',
            ...(sidebarExpanded
              ? {
                  px: '4px',
                  py: '3px',
                  minHeight: '36px',
                  width: '100%',
                }
              : {
                  px: 0,
                  py: '4px',
                  width: 'calc(100% - 16px)',
                  mx: 'auto',
                  minHeight: '30px',
                  height: '30px',
                }),

            '&:hover': {
              background: alpha(societySelectorColor, 0.12),
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              borderRadius: '4px',
              height: sidebarExpanded ? 22 : 18,
              width: sidebarExpanded ? 22 : 18,
              maxWidth: sidebarExpanded ? 22 : 18,
              maxHeight: sidebarExpanded ? 22 : 18,
              background: white[900],
            }}
          >
            <DocumentImage
              alt="Breed society logo"
              documentTypeCode={DocumentTypeCode.Logo}
              documentEntityId={
                selectedOrganisation?.organisation?.organisationEntityId || ''
              }
              fallbackImage={noLogoSvg}
              fill
              sx={{
                position: 'unset !important',
                borderRadius: '4px',
                height: 'auto !important',
                width: 'auto !important',
                maxHeight: '100%',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
          {sidebarExpanded && (
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                color: societySelectorColor,
                fontWeight: 400,
              }}
              variant="labellarge"
              noWrap
            >
              {selectedOrganisation?.organisation?.description}
            </Typography>
          )}
          {sidebarExpanded && (
            <ExpandMoreIcon
              sx={{
                flexShrink: 0,
                ml: 'auto',
                color: societySelectorColor,
                fontSize: '20px',
              }}
            />
          )}
        </Box>
      )}

      <Menu
        id="society-dropdown-menu"
        anchorEl={dropdownAnchor}
        open={Boolean(dropdownAnchor)}
        onClose={handleCloseDropdown}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { minWidth: SIDEBAR_WIDTH, maxHeight: 360 },
          },
        }}
        MenuListProps={{
          'aria-labelledby': 'society-dropdown-trigger',
          sx: { py: 0 },
        }}
      >
        <Box
          component="div"
          onClick={(e: MouseEvent) => e.stopPropagation()}
          sx={{
            px: 1,
            py: 0.5,
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder={intl.formatMessage({
              id: 'organisationSelector.search.placeholder',
              defaultMessage: 'Search societies',
            })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{ input: { 'aria-label': 'Search societies' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '12px',
              },
              '& .MuiOutlinedInput-input': {
                py: '4px',
                px: '8px',
                fontSize: '12px',
              },
            }}
          />
        </Box>
        {displayOrgs.length === 0 ? (
          <MenuItem
            disabled
            dense
            sx={{
              py: '6px',
              px: '10px',
              minHeight: 0,
              fontSize: '12px',
            }}
          >
            <Typography
              variant="bodysmall"
              color="text.secondary"
              sx={{ fontSize: '12px' }}
            >
              {searchQuery.trim()
                ? 'No societies match your search'
                : 'No societies'}
            </Typography>
          </MenuItem>
        ) : (
          <Box component="div">
            {displayOrgs.map((org) => (
              <MenuItem
                key={org.organisationId}
                dense
                selected={org.organisationId === selectedOrganisationId}
                onClick={() => handleSelectOrg(org)}
                sx={{
                  py: '6px',
                  px: '10px',
                  minHeight: 0,
                  fontSize: '12px',
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'text.primary',
                  },
                }}
              >
                <Typography noWrap sx={{ fontSize: '12px', width: '100%' }}>
                  {org.organisation?.description ??
                    org.organisation?.name ??
                    'Society'}
                </Typography>
              </MenuItem>
            ))}
            {showAllButtonVisible && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  dense
                  onClick={() => setShowAll(true)}
                  sx={{
                    justifyContent: 'center',
                    py: '6px',
                    minHeight: 0,
                    fontSize: '12px',
                    color: 'text.secondary',
                  }}
                >
                  <Typography sx={{ fontSize: '12px' }} color="text.secondary">
                    {intl.formatMessage({
                      id: 'organisationSelector.showAll',
                      defaultMessage: 'Show all',
                    })}
                  </Typography>
                </MenuItem>
              </>
            )}
          </Box>
        )}
      </Menu>

      <ShellSidebarLinks />

      <Button
        sx={{
          position: 'absolute',
          bottom: sidebarExpanded ? '8px' : '8px',
          height: '26px',
          minHeight: '26px',
          p: '1px',
          borderRadius: '4px',
          justifyContent: 'center',
          boxSizing: 'border-box',
          width: '26px',
          minWidth: '26px',
          ...(sidebarExpanded
            ? {
                right: '8px',
                left: 'auto',
              }
            : {
                left: '50%',
                right: 'auto',
                transform: 'translateX(-50%)',
              }),
          color: expandRailControlColor,
          background: 'transparent',
          border: `1px solid ${expandRailControlColor}`,
          '& .MuiSvgIcon-root': {
            fontSize: '16px',
          },
          '&:hover': {
            background: alpha(expandRailControlColor, 0.12),
            borderColor: expandRailControlColor,
            ...(sidebarExpanded ? {} : { transform: 'translateX(-50%)' }),
          },
        }}
        onClick={() => {
          setConfigMenuOpen(!sidebarExpanded);
          setSidebarExpanded(!sidebarExpanded);
        }}
      >
        {sidebarExpanded ? <SkipPreviousIcon /> : <SkipNextIcon />}
      </Button>
    </Box>
  );
}
